"use client";

import { cn } from "@newxsystems/shared";
import { useEffect, useRef, type CSSProperties } from "react";
import type { BlockImageEffect } from "@newxsystems/types";
import {
  supportsEffectWorker,
  type EffectInstance,
  type EffectFactory,
  type EffectPass,
} from "./EffectFx";

/**
 * The mount/host layer every effectful-hero effect renders into. Two drivers, one wiring:
 *
 * - worker mode: the canvas control is transferred to `EffectfulHeroEffectWorker`, which
 *   lazily loads the style's core and runs it on the canvas thread. The main thread
 *   only relays size/pointer and posts rAF ticks, so the animation loop never runs here.
 * - fallback mode (`create` given, used when OffscreenCanvas is missing or the worker
 *   failed): the same core runs on the main thread, driven by a local rAF loop —
 *   identical picture, no worker win.
 *
 * The stage reports readiness via `onReady`/`onFailed`; failure asks the stage to
 * switch to the legacy React component for that style.
 *
 * `paused` keeps the effect built but stops the frame loop (and pointer relay). One still
 * frame is drawn as soon as the effect is ready, so a paused overlay shows a finished
 * picture and resuming costs nothing but restarting the ticks.
 */
export const EffectFxHost = ({
  animation,
  create,
  onReady,
  onFailed,
  worker,
  containerClass,
  containerStyle,
  paused = false,
}: {
  animation: BlockImageEffect;
  /** Set only in fallback mode; worker mode needs nothing on the main thread. */
  create?: EffectFactory;
  onReady?: () => void;
  onFailed?: () => void;
  worker?: boolean;
  containerClass?: string;
  containerStyle?: CSSProperties;
  paused?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(paused);
  const setPlayingRef = useRef<((playing: boolean) => void) | null>(null);

  useEffect(() => {
    pausedRef.current = paused;
    setPlayingRef.current?.(!paused);
  }, [paused]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const canvas = document.createElement("canvas");
    canvas.className = "absolute inset-0 block h-full w-full";
    container.appendChild(canvas);

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const viewport = () => ({ vw: window.innerWidth || 1, vh: window.innerHeight || 1 });
    const bounds = () => ({
      width: Math.max(1, container.clientWidth),
      height: Math.max(1, container.clientHeight),
    });
    const pointerAt = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        cx: event.clientX,
        cy: event.clientY,
        inside:
          event.clientX >= rect.left &&
          event.clientY >= rect.top &&
          event.clientX <= rect.right &&
          event.clientY <= rect.bottom,
      };
    };

    let fx: EffectInstance | null = null;
    let workerInstance: Worker | null = null;
    let raf = 0;

    const runMain = () => {
      if (!create) return false;
      const pass: EffectPass = {
        ...bounds(),
        ...viewport(),
        dpr: window.devicePixelRatio || 1,
        reduceMotion,
      };
      fx = create(canvas, pass);
      fx.resize(pass.width, pass.height, pass.dpr);
      return true;
    };

    const startWorker = (): boolean => {
      if (!supportsEffectWorker(canvas)) return false;
      const offscreen = canvas.transferControlToOffscreen();
      const instance = new Worker(new URL("./EffectWorker.ts", import.meta.url), {
        type: "module",
      });
      instance.addEventListener(
        "message",
        (event: MessageEvent<{ type: string; message?: string }>) => {
          if (event.data.type === "ready") {
            if (pausedRef.current) instance.postMessage({ type: "frame", now: performance.now() });
            onReady?.();
          }
          if (event.data.type === "failed") onFailed?.();
        },
      );
      const inner = bounds();
      const outer = viewport();
      instance.postMessage(
        {
          type: "init",
          canvas: offscreen,
          animation,
          width: inner.width,
          height: inner.height,
          dpr: window.devicePixelRatio || 1,
          vw: outer.vw,
          vh: outer.vh,
          reduceMotion,
        },
        [offscreen],
      );
      workerInstance = instance;
      return true;
    };

    const runWorker = (): boolean => {
      if (!startWorker()) return false;
      // The animation runs because these ticks arrive; rAF stops while the tab is
      // hidden, which also pauses the worker for free.
      const tick = (now: number) => {
        workerInstance?.postMessage({ type: "frame", now });
        raf = requestAnimationFrame(tick);
      };
      setPlayingRef.current = (playing) => {
        cancelAnimationFrame(raf);
        raf = 0;
        if (playing) raf = requestAnimationFrame(tick);
      };
      if (!pausedRef.current) raf = requestAnimationFrame(tick);
      return true;
    };

    const runFallback = (): boolean => {
      if (!runMain()) return false;
      const tick = (now: number) => {
        fx?.frame(now);
        raf = requestAnimationFrame(tick);
      };
      setPlayingRef.current = (playing) => {
        cancelAnimationFrame(raf);
        raf = 0;
        if (playing) raf = requestAnimationFrame(tick);
      };
      if (pausedRef.current) fx?.frame(performance.now());
      else raf = requestAnimationFrame(tick);
      return true;
    };

    const onResize = () => {
      const inner = bounds();
      const outer = viewport();
      if (workerInstance) {
        workerInstance.postMessage({
          type: "resize",
          ...inner,
          ...outer,
          dpr: window.devicePixelRatio || 1,
        });
      } else {
        fx?.resize(inner.width, inner.height, window.devicePixelRatio || 1);
      }
    };

    const onPointerMove = (event: PointerEvent) => {
      if (pausedRef.current) return;
      const spot = pointerAt(event);
      if (workerInstance) {
        workerInstance.postMessage({ type: "pointer", ...spot });
      } else if (fx) {
        if (spot.inside) fx.pointer?.(spot.x, spot.y, spot.cx, spot.cy);
        else fx.pointerOut?.();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (pausedRef.current) return;
      const spot = pointerAt(event);
      if (!spot.inside) return;
      if (workerInstance) {
        workerInstance.postMessage({ type: "burst", x: spot.x, y: spot.y });
      } else {
        fx?.burst?.(spot.x, spot.y);
      }
    };
    const onPointerUp = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return;
      if (workerInstance) workerInstance.postMessage({ type: "pointer-out" });
      else fx?.pointerOut?.();
    };
    const onPointerOut = (event: PointerEvent) => {
      if (event.relatedTarget) return;
      if (workerInstance) workerInstance.postMessage({ type: "pointer-out" });
      else fx?.pointerOut?.();
    };
    const onWindowBlur = () => {
      if (workerInstance) workerInstance.postMessage({ type: "pointer-out" });
      else fx?.pointerOut?.();
    };
    const onVisibility = () => {
      // Hidden tabs stop rAF, which stops the worker ticks; returning replays a resize
      // so any missed geometry is picked up before the next frame.
      if (!document.hidden) onResize();
    };

    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(container);
    window.addEventListener("resize", onResize);
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("pointerup", onPointerUp, { passive: true });
    window.addEventListener("pointerout", onPointerOut, { passive: true });
    window.addEventListener("blur", onWindowBlur);
    document.addEventListener("visibilitychange", onVisibility);

    let started = false;
    if (worker) {
      started = runWorker();
    } else {
      started = runFallback();
    }
    if (started && !worker) onReady?.();
    if (!started) onFailed?.();

    return () => {
      setPlayingRef.current = null;
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointerout", onPointerOut);
      window.removeEventListener("blur", onWindowBlur);
      document.removeEventListener("visibilitychange", onVisibility);
      if (workerInstance) {
        workerInstance.terminate();
      } else {
        fx?.dispose();
      }
      if (canvas.parentNode === container) container.removeChild(canvas);
    };
  }, [animation, create, onReady, onFailed, worker]);

  return (
    <div
      ref={containerRef}
      className={cn("relative h-full w-full overflow-hidden", containerClass)}
      style={containerStyle}
    />
  );
};

EffectFxHost.displayName = "EffectfulHeroBlock::EffectFxHost";
